/**
 * CartDrawer — Sliding bag drawer from the right.
 * Uses Radix Sheet for accessible overlay behavior.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/features/cart/store/cart-store";
import { CartItem } from "@/features/cart/components/cart-item";
import { FreeShippingBar } from "@/features/cart/components/free-shipping-bar";
import { formatPrice } from "@/components/forms/price-display";

import { useNavigate } from "@tanstack/react-router";
import { openRazorpayMagicCheckout } from "@/lib/razorpay";
import { useCurrentUser } from "@/hooks/use-auth";

import { RazorpayAddressModal } from "@/components/checkout/RazorpayAddressModal";

export function CartDrawer() {
  const { lines, open, setOpen, count, subtotal, clear } = useCart();
  const { user, isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const handleRazorpayCheckout = () => {
    setOpen(false);
    setAddressModalOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="h-5 w-5" strokeWidth={1.25} />
            Your Bag
            {count > 0 && (
              <span className="text-sm text-muted-foreground font-sans font-normal">({count})</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
              <ShoppingBag className="h-7 w-7 text-muted-foreground" strokeWidth={1.25} />
            </div>
            <p className="mt-4 font-display text-lg">Your bag is empty</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add your first botanical piece to get started.
            </p>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex min-h-11 items-center bg-primary px-8 text-xs tracking-[0.18em] text-primary-foreground uppercase transition-opacity hover:opacity-90"
            >
              Browse collection
            </Link>
          </div>
        ) : (
          <>
            <FreeShippingBar subtotal={subtotal} className="mt-4" />

            <ul className="flex-1 divide-y overflow-y-auto py-2">
              {lines.map(({ product, qty }) => (
                <CartItem key={product.id} product={product} qty={qty} />
              ))}
            </ul>

            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-display text-lg tabular-nums">{formatPrice(subtotal)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Shipping and taxes calculated at checkout.
              </p>
              <button
                onClick={handleRazorpayCheckout}
                className="flex min-h-12 w-full items-center justify-center gap-2 bg-emerald-900 text-xs tracking-[0.16em] text-white font-bold uppercase transition-[color,background-color,border-color,box-shadow,transform,opacity] hover:bg-emerald-800 active:scale-[0.99] rounded-xl shadow-md cursor-pointer"
              >
                <span>Pay with Razorpay — {formatPrice(subtotal)}</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </SheetContent>

      {/* 1-Click Razorpay Address & Payment Modal */}
      <RazorpayAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        items={lines.map((l) => ({
          id: l.product.id,
          name: l.product.name,
          price: l.product.price,
          qty: l.qty,
          image: l.product.image,
        }))}
        totalAmount={subtotal}
      />
    </Sheet>
  );
}
